export const ATLAS_PAGE_SIZE = 1024
export const ATLAS_MAX_PAGES = 4
export const ATLAS_MAX_GLYPH_KEYS = 2048
export const WEBGL_ATLAS_MAX_BYTES = ATLAS_PAGE_SIZE * ATLAS_PAGE_SIZE * ATLAS_MAX_PAGES
export const CANVAS_ATLAS_MAX_BYTES = WEBGL_ATLAS_MAX_BYTES * 4

export interface GlyphKey {
  readonly codepoint: number
  readonly variant: 0 | 1 | 2 | 3
  readonly embolden: boolean
  readonly shear: boolean
}

export interface GlyphRasterRequest extends GlyphKey {
  readonly configGeneration: number
  readonly dpr: number
}

export interface GlyphBitmap {
  readonly width: number
  readonly height: number
  /** Logical offset from the cell's left edge. */
  readonly offsetX: number
  /** Logical offset from the authoritative server baseline. */
  readonly offsetY: number
  /** One coverage byte per physical backing pixel. */
  readonly alpha: Uint8Array
}

export interface GlyphRasterizer {
  rasterize(request: GlyphRasterRequest): Promise<GlyphBitmap>
}

export interface AtlasPageStorage<Page> {
  createPage(index: number, size: number): Page
  clearPage(page: Page): void
  destroyPage(page: Page): void
  upload(page: Page, x: number, y: number, bitmap: GlyphBitmap): void
}

export interface AtlasGlyph<Page> extends GlyphKey {
  readonly kind: 'atlas'
  readonly configGeneration: number
  readonly dpr: number
  readonly pageIndex: number
  /** Version of the page contents when this handle was reserved. */
  readonly pageGeneration: number
  readonly page: Page
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly offsetX: number
  readonly offsetY: number
}

export interface AtlasUncachedGlyph {
  readonly kind: 'uncached'
  readonly request: GlyphRasterRequest
  readonly bitmap: GlyphBitmap
}

export type GlyphAtlasResult<Page> = AtlasGlyph<Page> | AtlasUncachedGlyph | null

interface AtlasPage<Page> {
  readonly index: number
  readonly value: Page
  generation: number
  shelfX: number
  shelfY: number
  shelfHeight: number
  lastUsed: number
}

interface Placement<Page> {
  readonly page: AtlasPage<Page>
  readonly x: number
  readonly y: number
}

function validKey(request: GlyphKey): boolean {
  return Number.isInteger(request.codepoint) && request.codepoint >= 0 && request.codepoint <= 0x10ffff &&
    !(request.codepoint >= 0xd800 && request.codepoint <= 0xdfff) &&
    Number.isInteger(request.variant) && request.variant >= 0 && request.variant <= 3 &&
    typeof request.embolden === 'boolean' && typeof request.shear === 'boolean'
}

function keyOf(request: GlyphKey): string {
  return `${request.codepoint}/${request.variant}/${request.embolden ? 1 : 0}/${request.shear ? 1 : 0}`
}

function validBitmap(bitmap: GlyphBitmap): boolean {
  return Number.isInteger(bitmap.width) && bitmap.width >= 0 &&
    Number.isInteger(bitmap.height) && bitmap.height >= 0 &&
    Number.isFinite(bitmap.offsetX) && Number.isFinite(bitmap.offsetY) &&
    bitmap.alpha instanceof Uint8Array && bitmap.alpha.byteLength === bitmap.width * bitmap.height
}

export class GlyphAtlas<Page> {
  #configGeneration: number
  #dpr: number
  #epoch = 1
  #tick = 0
  #disposed = false
  readonly #rasterizer: GlyphRasterizer
  readonly #storage: AtlasPageStorage<Page>
  readonly #pages: AtlasPage<Page>[] = []
  readonly #glyphs = new Map<string, AtlasGlyph<Page>>()
  readonly #pending = new Map<string, Promise<GlyphAtlasResult<Page>>>()

  constructor(configGeneration: number, dpr: number, rasterizer: GlyphRasterizer,
              storage: AtlasPageStorage<Page>) {
    this.#validateConfiguration(configGeneration, dpr)
    this.#configGeneration = configGeneration
    this.#dpr = dpr
    this.#rasterizer = rasterizer
    this.#storage = storage
  }

  get configGeneration(): number { return this.#configGeneration }
  get dpr(): number { return this.#dpr }
  get stats(): Readonly<{ pages: number; keys: number; pending: number }> {
    return Object.freeze({ pages: this.#pages.length, keys: this.#glyphs.size, pending: this.#pending.size })
  }

  lookup(request: GlyphKey): AtlasGlyph<Page> | null {
    if (this.#disposed || !validKey(request)) return null
    return this.#glyphs.get(keyOf(request)) ?? null
  }

  /**
   * Atlas handles are immediate-consumption values. Consumers must call this
   * immediately before using a handle because spatial page recycling
   * invalidates every prior handle into that page.
   */
  isCurrent(glyph: AtlasGlyph<Page>): boolean {
    const page = this.#pages[glyph.pageIndex]
    return !this.#disposed && page !== undefined && page.value === glyph.page &&
      page.generation === glyph.pageGeneration &&
      glyph.configGeneration === this.#configGeneration && glyph.dpr === this.#dpr &&
      this.#glyphs.get(keyOf(glyph)) === glyph
  }

  get(request: GlyphKey): Promise<GlyphAtlasResult<Page>> {
    if (this.#disposed) return Promise.reject(new Error('glyph atlas is disposed'))
    if (!validKey(request)) return Promise.resolve(null)
    const key = keyOf(request)
    const existing = this.#glyphs.get(key)
    if (existing !== undefined) return Promise.resolve(existing)
    const inFlight = this.#pending.get(key)
    if (inFlight !== undefined) return inFlight
    // Match native reserve semantics: key-table exhaustion does not recycle a
    // spatially usable page. The caller may render this glyph uncached.
    if (this.#glyphs.size + this.#pending.size >= ATLAS_MAX_GLYPH_KEYS) return Promise.resolve(null)

    const epoch = this.#epoch
    const fullRequest = Object.freeze({
      ...request, configGeneration: this.#configGeneration, dpr: this.#dpr,
    })
    const promise = this.#rasterizeAndReserve(key, fullRequest, epoch)
    this.#pending.set(key, promise)
    const cleanup = (): void => {
      if (this.#pending.get(key) === promise) this.#pending.delete(key)
    }
    void promise.then(cleanup, cleanup)
    return promise
  }

  reconfigure(configGeneration: number, dpr: number): void {
    if (this.#disposed) throw new Error('glyph atlas is disposed')
    this.#validateConfiguration(configGeneration, dpr)
    if (configGeneration === this.#configGeneration && dpr === this.#dpr) return
    this.#configGeneration = configGeneration
    this.#dpr = dpr
    this.#reset()
  }

  dispose(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.#reset()
  }

  async #rasterizeAndReserve(key: string, request: GlyphRasterRequest,
                             epoch: number): Promise<GlyphAtlasResult<Page>> {
    const bitmap = await this.#rasterizer.rasterize(request)
    if (epoch !== this.#epoch) return null
    if (!validBitmap(bitmap)) {
      throw new RangeError('glyph atlas rasterizer returned an invalid bitmap')
    }
    if (bitmap.width > ATLAS_PAGE_SIZE || bitmap.height > ATLAS_PAGE_SIZE) {
      return Object.freeze({ kind: 'uncached', request, bitmap })
    }
    const placement = this.#place(bitmap.width, bitmap.height)
    if (placement === null || epoch !== this.#epoch) return null
    if (bitmap.width > 0 && bitmap.height > 0) {
      this.#storage.upload(placement.page.value, placement.x, placement.y, bitmap)
    }
    if (epoch !== this.#epoch) return null
    placement.page.lastUsed = ++this.#tick
    const glyph: AtlasGlyph<Page> = Object.freeze({
      kind: 'atlas',
      codepoint: request.codepoint,
      variant: request.variant,
      embolden: request.embolden,
      shear: request.shear,
      configGeneration: request.configGeneration,
      dpr: request.dpr,
      pageIndex: placement.page.index,
      pageGeneration: placement.page.generation,
      page: placement.page.value,
      x: placement.x,
      y: placement.y,
      width: bitmap.width,
      height: bitmap.height,
      offsetX: bitmap.offsetX,
      offsetY: bitmap.offsetY,
    })
    this.#glyphs.set(key, glyph)
    return glyph
  }

  #place(width: number, height: number): Placement<Page> | null {
    const needsArea = width > 0 && height > 0
    for (const page of this.#pages) {
      if (!needsArea) return { page, x: 0, y: 0 }
      const placement = this.#placeOnPage(page, width, height)
      if (placement !== null) return placement
    }
    if (this.#pages.length < ATLAS_MAX_PAGES) {
      const page: AtlasPage<Page> = {
        index: this.#pages.length,
        value: this.#storage.createPage(this.#pages.length, ATLAS_PAGE_SIZE),
        generation: 1, shelfX: 0, shelfY: 0, shelfHeight: 0, lastUsed: 0,
      }
      this.#pages.push(page)
      if (!needsArea) return { page, x: 0, y: 0 }
      return this.#placeOnPage(page, width, height)
    }

    const page = this.#recycleLruPage()
    if (page === null) return null
    if (!needsArea) return { page, x: 0, y: 0 }
    return this.#placeOnPage(page, width, height)
  }

  #recycleLruPage(): AtlasPage<Page> | null {
    if (this.#pages.length === 0) return null
    let page = this.#pages[0]
    for (let index = 1; index < this.#pages.length; index++) {
      if (this.#pages[index].lastUsed < page.lastUsed) page = this.#pages[index]
    }
    page.generation++
    for (const [key, glyph] of this.#glyphs) {
      if (glyph.pageIndex === page.index) this.#glyphs.delete(key)
    }
    page.shelfX = 0
    page.shelfY = 0
    page.shelfHeight = 0
    this.#storage.clearPage(page.value)
    return page
  }

  #placeOnPage(page: AtlasPage<Page>, width: number, height: number): Placement<Page> | null {
    let shelfX = page.shelfX
    let shelfY = page.shelfY
    let shelfHeight = page.shelfHeight
    if (shelfX + width > ATLAS_PAGE_SIZE) {
      shelfY += shelfHeight
      shelfX = 0
      shelfHeight = 0
    }
    if (shelfY + height > ATLAS_PAGE_SIZE) return null
    const placement = { page, x: shelfX, y: shelfY }
    page.shelfX = shelfX + width
    page.shelfY = shelfY
    page.shelfHeight = Math.max(shelfHeight, height)
    return placement
  }

  #reset(): void {
    ++this.#epoch
    this.#glyphs.clear()
    this.#pending.clear()
    const pages = this.#pages.splice(0)
    this.#tick = 0
    let firstError: unknown
    let failed = false
    for (const page of pages) {
      page.generation++
      try {
        this.#storage.destroyPage(page.value)
      } catch (error) {
        if (!failed) firstError = error
        failed = true
      }
    }
    if (failed) throw firstError
  }

  #validateConfiguration(configGeneration: number, dpr: number): void {
    if (!Number.isInteger(configGeneration) || configGeneration < 0 || configGeneration > 0xffffffff) {
      throw new RangeError('atlas configuration generation is invalid')
    }
    if (!Number.isFinite(dpr) || dpr <= 0 || dpr > 16) throw new RangeError('atlas DPR is invalid')
  }
}
